import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import api from "../../utils/api";
import { getImageSrc } from "../../utils/getImageSrc";
import { formatRate } from "../../utils/formatters";
import { getEarnedBadges } from "../../utils/badges";
import { useCompare } from "../../providers/CompareProvider";
import "./ComparePage.css";

const MODE_LABELS = { online: "Online", hybrid: "Hybrid", physical: "Physical" };
const formatModes = (modes = []) =>
  modes.length
    ? modes.map((m) => MODE_LABELS[m] || m).join(", ")
    : "Not specified";

const ComparePage = () => {
  const { selected, remove } = useCompare();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    if (selected.length === 0) {
      setProviders([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.allSettled(
      selected.map((s) => api.get(`/users/public-profile/${s.slug || s.id}`)),
    ).then((results) => {
      if (cancelled) return;
      const loaded = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value.data);
      setProviders(loaded);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [selected]);

  const isAcademy = (p) => p.role === "institution";

  const rows = [
    {
      label: "Subjects",
      render: (p) =>
        (p.allCourses?.length ? p.allCourses : p.selectedCategories || [])
          .slice(0, 6)
          .join(", ") || "Not specified",
    },
    { label: "Location", render: (p) => p.generalLocation || "Not specified" },
    { label: "Delivery Mode", render: (p) => formatModes(p.teachingModes) },
    {
      label: "Rate",
      render: (p) =>
        p.pricingSummary?.lowestRate
          ? formatRate(p.pricingSummary.lowestRate, p.pricingSummary.lowestPeriod)
          : "Rate on request",
    },
    {
      label: "Verification",
      render: (p) => {
        const earned = getEarnedBadges(p);
        return earned.length
          ? earned.map((b) => `${b.icon} ${b.label}`).join(", ")
          : "Not verified yet";
      },
    },
    {
      label: "Rating & Reviews",
      render: (p) =>
        p.reviewCount > 0
          ? `⭐ ${Number(p.averageRating).toFixed(1)}/5 (${p.reviewCount} review${p.reviewCount === 1 ? "" : "s"})`
          : "New on TutorsLinks",
    },
    {
      label: "Experience",
      render: (p) => p.bioExperience?.trim() || "Not specified",
    },
    {
      label: "Availability",
      render: (p) => p.availability?.trim() || "Not specified",
    },
    {
      label: "Free Intro Session",
      render: (p) => (p.offersIntroSession ? "✅ Available" : "Not offered"),
    },
  ];

  return (
    <div className="tutors-app">
      <Helmet>
        <title>Compare Tutors & Training Centres | TutorsLinks</title>
        <meta
          name="robots"
          content="noindex"
        />
      </Helmet>

      <Header />

      <main className="compare-page">
        <div className="compare-page-container">
          <h1 className="compare-page-title">Compare Tutors & Training Centres</h1>

          {loading ? (
            <p className="compare-empty-state">Loading comparison…</p>
          ) : providers.length < 2 ? (
            <div className="compare-empty-state">
              <p>
                {providers.length === 0
                  ? "You haven't selected anyone to compare yet."
                  : "Select at least one more tutor or training centre to compare."}
              </p>
              <button
                type="button"
                className="compare-back-btn"
                onClick={() => navigate("/search")}
              >
                Browse Tutors & Centres
              </button>
            </div>
          ) : (
            <div className="compare-table-scroll">
              <table className="compare-table">
                <thead>
                  <tr>
                    <th className="compare-row-label-col" />
                    {providers.map((p) => (
                      <th key={p._id} className="compare-col-header">
                        <img
                          src={getImageSrc(p.picture)}
                          alt={p.displayName}
                          onError={(e) => {
                            e.target.src = "/images/avatar3.png";
                          }}
                        />
                        <span className="compare-col-name">{p.displayName}</span>
                        <Link
                          to={isAcademy(p) ? `/academy/${p.slug || p._id}` : `/tutor/${p.slug || p._id}`}
                          className="compare-col-view-link"
                        >
                          View Full Profile
                        </Link>
                        <button
                          type="button"
                          className="compare-col-remove"
                          onClick={() => remove(p._id)}
                        >
                          Remove
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.label}>
                      <td className="compare-row-label-col">{row.label}</td>
                      {providers.map((p) => (
                        <td key={p._id}>{row.render(p)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ComparePage;
